import { request } from './http'

export interface SkeletonLayoutVolume {
  volume_no: number
  start_chapter_no: number
  end_chapter_no: number
  word_budget: number
}

export interface SkeletonLayout {
  total_chapters: number
  volume_count: number
  volumes: SkeletonLayoutVolume[]
}

export interface ForeshadowItem {
  is_long_term?: boolean
  key?: string
  detail?: string
}

export interface VolumeSkeleton {
  volume_no: number
  start_chapter_no: number
  end_chapter_no: number
  word_budget: number
  outline: string
  milestones: string[]
  foreshadow_plant: ForeshadowItem[]
  foreshadow_payoff: ForeshadowItem[]
  [key: string]: unknown
}

export interface ProposeSkeletonInput {
  target_words?: number
  chapter_word_target?: number
}

export interface ConfirmSkeletonInput {
  chapter_word_target: number
  volumes: VolumeSkeleton[]
}

export interface SkeletonJobSubmitResponse {
  job_id: string
  status: 'queued' | string
}

export interface ProposeSkeletonResponse {
  layout: SkeletonLayout
  volumes: VolumeSkeleton[]
}

export interface SkeletonResponse {
  volumes: VolumeSkeleton[] | null
}

export interface SkeletonJobResult {
  layout?: SkeletonLayout
  volumes?: VolumeSkeleton[]
}

export interface SkeletonJobStatus {
  job_id: string
  project_id: string
  op: 'propose' | 'confirm' | string
  status: 'queued' | 'running' | 'done' | 'failed' | string
  progress?: string
  result?: SkeletonJobResult
  error?: string
  error_code?: string
  retryable?: boolean
}

export const skeletonApi = {
  propose: (projectId: string, body: ProposeSkeletonInput): Promise<SkeletonJobSubmitResponse> =>
    request<SkeletonJobSubmitResponse>(`/v1/projects/${encodeURIComponent(projectId)}/skeleton/propose`, {
      method: 'POST',
      body,
    }),

  confirm: (projectId: string, body: ConfirmSkeletonInput): Promise<SkeletonJobSubmitResponse> =>
    request<SkeletonJobSubmitResponse>(`/v1/projects/${encodeURIComponent(projectId)}/skeleton/confirm`, {
      method: 'POST',
      body,
    }),

  job: (projectId: string, jobId: string): Promise<SkeletonJobStatus> =>
    request<SkeletonJobStatus>(
      `/v1/projects/${encodeURIComponent(projectId)}/skeleton/job/${encodeURIComponent(jobId)}`,
    ),

  update: (projectId: string, body: ConfirmSkeletonInput): Promise<VolumeSkeleton[]> =>
    request<SkeletonResponse>(`/v1/projects/${encodeURIComponent(projectId)}/skeleton`, {
      method: 'PUT',
      body,
    }).then((data) => data.volumes ?? []),

  get: (projectId: string): Promise<VolumeSkeleton[]> =>
    request<SkeletonResponse>(`/v1/projects/${encodeURIComponent(projectId)}/skeleton`).then(
      (data) => data.volumes ?? [],
    ),
}
